import { useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useCorporateDocuments,
  useUploadCorporateDocument,
  useUpdateCorporateDocument,
  useDeleteCorporateDocument,
  useUploadCorporateDocumentFile,
  useDeleteCorporateDocumentFile,
  useDocumentationPills,
  useCreatePill,
  useUpdatePill,
  useDeletePill,
  getRecognitionErrorMessage,
} from '@/api/recognition';
import type {
  CorporateDocType,
  CorporateDocumentDto,
  UpdateCorporateDocumentBody,
  DocumentationPillDto,
  PillContentType,
  UpsertPillBody,
} from '@/api/recognition';
import { translationApi, SessionExpiredError } from '@/api/client';
import type { OUItem } from '@/api/types';
import { SUPPORTED_LANGUAGE_CODES, SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { Modal, ModalHeader, ModalBody, ModalFooter, ConfirmModal } from '@/components/common';
import { OuTreeSelect } from '@/components/common/OuTree';
// Import directo del archivo (no del barrel `@/components/common/OuTree`, que algunos tests
// reemplazan por un stub de solo `OuTreeSelect` y tumbarían este hook si viniera del mismo módulo).
import { useDropdownPosition } from '@/components/common/OuTree/useDropdownPosition';
// Reusa el shell visual (.scope-pop / .scope-pop-scrim / .scope-pop-title) de ScopePopover para
// LangVersionsPopover, sin duplicar esas reglas ni tocar el componente ScopePopover en sí.
import '@/components/common/ScopePopover/ScopePopover.css';
import { useOUItems } from '@/hooks/useOUs';
import { AdminSection, LocalizedFields, usePagination, AdminPagerFooter } from './adminShared';

/** Tipos de documento corporativo (maqueta §11). */
const CORP_TYPES: CorporateDocType[] = ['PoliticaPrivacidad', 'BasesLegales', 'GuiaUsuario', 'Premios', 'CookiesPolicy', 'LegalNotice'];
// GAP-DOC (§26.11) — la maqueta separa "Docs Legales" (obligatorios/aceptación) de "Biblioteca" (resto).
const LEGAL_TYPES: CorporateDocType[] = ['PoliticaPrivacidad', 'BasesLegales', 'CookiesPolicy', 'LegalNotice'];
const isLegalType = (t: CorporateDocType) => LEGAL_TYPES.includes(t);
/** Límite del PDF (RN-D01: solo PDF ≤ 25 MiB) — validación en cliente antes de subir. */
const MAX_PDF_BYTES = 25 * 1024 * 1024;

/** Tipo de archivo de la Biblioteca (1:1 maqueta: sub-tabs Todos/PDF/Video/Imagen). Sale del `mediaKind`
 *  del backend (Pdf/Video/Image); Video e Imagen se soportan como URL externa (UserAdminService #46). */
type LibType = 'todos' | 'pdf' | 'video' | 'imagen';
function docFileType(d: CorporateDocumentDto): Exclude<LibType, 'todos'> {
  // Tipo real del backend (mediaKind = 'Pdf'|'Video'|'Image'); fallback a la extensión por compat.
  const kind = (d.mediaKind ?? '').toLowerCase();
  if (kind === 'video') return 'video';
  if (kind === 'image') return 'imagen';
  if (kind === 'pdf') return 'pdf';
  const n = (d.fileName ?? '').toLowerCase();
  if (/\.(mp4|mov|webm|avi)$/.test(n)) return 'video';
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(n)) return 'imagen';
  return 'pdf';
}
const LIB_TYPE_TABS: { key: LibType; icon: string }[] = [
  { key: 'todos', icon: 'fa-layer-group' },
  { key: 'pdf', icon: 'fa-file-pdf' },
  { key: 'video', icon: 'fa-video' },
  { key: 'imagen', icon: 'fa-image' },
];
const LIB_TYPE_ICON: Record<Exclude<LibType, 'todos'>, string> = { pdf: 'fa-file-pdf', video: 'fa-video', imagen: 'fa-image' };

/** Selector de idiomas (chips con checkbox) reutilizado por el alta y la edición. */
function LanguageChips({
  value, onToggle,
}: Readonly<{ value: string[]; onToggle: (code: string) => void }>) {
  return (
    <div className="admin-docs-corp-langs">
      {SUPPORTED_LANGUAGES.map((lang) => (
        <label key={lang.code} className="admin-docs-corp-lang">
          <input
            type="checkbox"
            checked={value.includes(lang.code)}
            onChange={() => onToggle(lang.code)}
          />
          <span>{lang.flag} {lang.displayCode}</span>
        </label>
      ))}
    </div>
  );
}

/** Modal de edición de METADATA de un documento corporativo (no reemplaza el archivo). */
function CorporateDocumentEditModal({
  doc, ouItems, availableTypes, onClose, onSave, saving, error,
}: Readonly<{
  doc: CorporateDocumentDto;
  ouItems: OUItem[];
  availableTypes: CorporateDocType[];
  onClose: () => void;
  onSave: (body: UpdateCorporateDocumentBody) => void;
  saving: boolean;
  error: string | null;
}>) {
  const { t } = useTranslation('recognition');
  const [nombre, setNombre] = useState(doc.nombre);
  const [tipo, setTipo] = useState<CorporateDocType>(doc.tipo);
  const [scopeOuIds, setScopeOuIds] = useState<string[]>(doc.scopeOuIds);
  const [languages, setLanguages] = useState<string[]>(doc.languages);
  // GAP-DOC — hay que preservar/editar las banderas legales: sin enviarlas, el PUT las reiniciaría
  // a false (el handler siempre asigna doc.FirstLogin = command.FirstLogin, default false).
  const [firstLogin, setFirstLogin] = useState(doc.firstLogin ?? false);
  const [triggerUpdate, setTriggerUpdate] = useState(doc.triggerUpdate ?? false);

  const toggleLanguage = (code: string) =>
    setLanguages((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  const canSave = nombre.trim().length > 0 && scopeOuIds.length > 0 && !saving;

  return (
    <Modal open onClose={onClose} labelledBy="corp-edit-title">
      <ModalHeader onClose={onClose}>
        <span id="corp-edit-title">{t('admin.docs.corp.editar')}</span>
      </ModalHeader>
      <ModalBody>
        <label className="adm-field">
          <span>{t('admin.docs.corp.nombre')}</span>
          <input type="text" value={nombre} maxLength={200} onChange={(e) => setNombre(e.target.value)} />
        </label>
        <label className="adm-field">
          <span>{t('admin.docs.corp.tipo')}</span>
          <select className="field" value={tipo} onChange={(e) => setTipo(e.target.value as CorporateDocType)}>
            {availableTypes.map((ty) => (
              <option key={ty} value={ty}>{t(`admin.docs.corp.tipos.${ty}`)}</option>
            ))}
          </select>
        </label>
        {/* Banderas de aceptación: solo aplican a los tipos legales (obligatorios). */}
        {isLegalType(tipo) && (
          <div className="adm-toggles-row">
            <label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" checked={firstLogin} onChange={(e) => setFirstLogin(e.target.checked)} /> {t('admin.docs.corp.firstLogin')}</label>
            <label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" checked={triggerUpdate} onChange={(e) => setTriggerUpdate(e.target.checked)} /> {t('admin.docs.corp.triggerUpdate')}</label>
          </div>
        )}
        <div className="adm-field" role="group" aria-labelledby="corp-edit-ou-label">
          <span id="corp-edit-ou-label">{t('admin.docs.corp.visibilidad')}</span>
          <OuTreeSelect items={ouItems} mode="multi" variant="inline" value={scopeOuIds} onChange={setScopeOuIds} />
        </div>
        <div className="adm-field" role="group" aria-labelledby="corp-edit-lang-label">
          <span id="corp-edit-lang-label">{t('admin.docs.corp.idiomas')}</span>
          <LanguageChips value={languages} onToggle={toggleLanguage} />
        </div>
        {error && (
          <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {error}</p>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          {t('admin.docs.corp.cancelar')}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!canSave}
          onClick={() => onSave({ nombre: nombre.trim(), tipo, scopeOuIds, languages, firstLogin: isLegalType(tipo) ? firstLogin : false, triggerUpdate: isLegalType(tipo) ? triggerUpdate : false })}
        >
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} /> {t('admin.docs.corp.guardar')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Contenido de una versión por idioma (subir/reemplazar/quitar un PDF sobre un documento): si el
 * idioma ya tiene versión → preview (ojo) + reemplazar + quitar; si no → botón Subir. Estado de
 * carga propio (spinner) independiente por instancia. Sin wrapper de tabla — lo reutiliza el
 * popover de idiomas (`LangVersionsPopover`), tanto en Docs Legales como en Biblioteca.
 */
function LangFileControl({ doc, lang }: Readonly<{ doc: CorporateDocumentDto; lang: string }>) {
  const { t } = useTranslation('recognition');
  const uploadFile = useUploadCorporateDocumentFile();
  const deleteFile = useDeleteCorporateDocumentFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const entry = doc.files?.[lang];
  const busy = uploadFile.isPending || deleteFile.isPending;

  // Mismo guard cliente que el alta principal: PDF + ≤25 MiB antes de gastar la subida (backend RN-D01).
  const pick = (f: File | null) => {
    if (!f) return;
    const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    if (!isPdf || f.size <= 0 || f.size > MAX_PDF_BYTES) { setLocalErr(t('admin.docs.corp.soloPdf')); return; }
    setLocalErr(null);
    uploadFile.mutate({ id: doc.id, lang, file: f });
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="admin-docs-hidden-input"
        onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = ''; }}
      />
      {entry?.url ? (
        <span className="admin-docs-langslot">
          <a className="btn btn--link" href={entry.url} target="_blank" rel="noopener noreferrer" title={entry.fileName} aria-label={t('admin.docs.corp.verVersion')}>
            <i className="fas fa-eye" />
          </a>
          {/* Descarga POR IDIOMA (no a nivel de fila): con varios idiomas cargados no hay un solo
              "archivo del documento" — descargar desde la fila descargaba siempre el archivo raíz
              (doc.url), sin relación con el idioma que el admin esperaba. */}
          <a className="btn btn--link" href={entry.url} download={entry.fileName} target="_blank" rel="noopener noreferrer" title={entry.fileName} aria-label={t('admin.docs.corp.descargar')}>
            <i className="fas fa-download" />
          </a>
          <button type="button" className="btn btn--link" disabled={busy} onClick={() => fileRef.current?.click()} aria-label={t('admin.docs.corp.reemplazar')}>
            <i className={`fas ${uploadFile.isPending ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'}`} />
          </button>
          <button type="button" className="btn btn--danger" disabled={busy} onClick={() => deleteFile.mutate({ id: doc.id, lang })} aria-label={t('admin.docs.corp.quitarVersion')}>
            <i className={`fas ${deleteFile.isPending ? 'fa-spinner fa-spin' : 'fa-xmark'}`} />
          </button>
        </span>
      ) : (
        <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
          <i className={`fas ${uploadFile.isPending ? 'fa-spinner fa-spin' : 'fa-upload'}`} /> {t('admin.docs.corp.subirVersion')}
        </button>
      )}
      {(localErr || uploadFile.isError || deleteFile.isError) && (
        <span className="admin-docs-langslot-err" title={localErr ?? getRecognitionErrorMessage(uploadFile.error || deleteFile.error, t('admin.docs.corp.errorSubir'))}>
          <i className="fas fa-triangle-exclamation" />
        </span>
      )}
    </>
  );
}

/**
 * Popover de "Versiones por idioma": los 7 idiomas del catálogo no caben como columnas fijas, así
 * que se listan en un popover anclado al trigger de la fila. Compartido por Biblioteca y Docs
 * Legales (antes esta última tenía 2 columnas fijas ES/EN — RN-D02 no distingue tipo de documento
 * para esto, y limitarlo a 2 idiomas dejaba a Europa sin poder cargar fr/de/nl/ro). Mismo mecanismo
 * de posicionamiento/portal/scrim que `components/common/ScopePopover` (no se toca ese componente:
 * su `items: string[]` no admite acciones por fila, así que este es un componente hermano, pequeño,
 * con contenido propio).
 */
function LangVersionsPopover({ doc }: Readonly<{ doc: CorporateDocumentDto }>) {
  const { t } = useTranslation('recognition');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const position = useDropdownPosition(triggerRef, open);
  const count = Object.keys(doc.files ?? {}).length;

  const popoverStyle = position
    ? ({
        '--scope-pop-top': `${position.top}px`,
        '--scope-pop-left': `${position.left}px`,
        '--scope-pop-width': `${position.width}px`,
        '--scope-pop-max-height': `${position.maxHeight}px`,
      } as CSSProperties)
    : undefined;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="admin-docs-lang-versions-trigger"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {t('admin.docs.corp.versionesPorIdiomaCount', { count, total: SUPPORTED_LANGUAGES.length })}
        <i className={`fas fa-chevron-down admin-docs-lang-versions-caret ${open ? 'admin-docs-lang-versions-caret--open' : ''}`} aria-hidden="true" />
      </button>
      {open && position && createPortal(
        <>
          <div className="scope-pop-scrim" onClick={() => setOpen(false)} />
          <div className="scope-pop admin-docs-lang-versions-pop" style={popoverStyle} role="dialog" aria-label={t('admin.docs.corp.versionesPorIdioma')}>
            <div className="scope-pop-title">{t('admin.docs.corp.versionesPorIdioma')}</div>
            <ul className="admin-docs-lang-versions-list">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <li key={lang.code} className="admin-docs-lang-versions-row">
                  <span className="admin-docs-lang-versions-label">{lang.flag} {lang.displayCode}</span>
                  <LangFileControl doc={doc} lang={lang.code} />
                </li>
              ))}
            </ul>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/** RF-079 — repositorio de PDFs corporativos por OU: alta (multipart) + lista + editar/eliminar. */
function CorporateDocumentsManager({ legalOnly = false }: Readonly<{ legalOnly?: boolean }>) {
  const { t, i18n } = useTranslation('recognition');
  const displayLang = i18n.language?.split('-')[0] ?? 'es';
  const list = useCorporateDocuments();
  const upload = useUploadCorporateDocument();
  const update = useUpdateCorporateDocument();
  const remove = useDeleteCorporateDocument();
  const { data: ouItems = [] } = useOUItems();
  const fileRef = useRef<HTMLInputElement>(null);

  // GAP-DOC — según la pestaña: "Docs Legales" (legalOnly) o "Biblioteca" (resto).
  const availableTypes = legalOnly ? LEGAL_TYPES : CORP_TYPES.filter((ty) => !isLegalType(ty));
  const defaultTipo = availableTypes[0];

  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<CorporateDocType>(defaultTipo);
  const [scopeOuIds, setScopeOuIds] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [firstLogin, setFirstLogin] = useState(false);
  const [triggerUpdate, setTriggerUpdate] = useState(false);
  // Biblioteca: tipo de medio del alta (PDF sube archivo; Video/Imagen usan URL externa).
  const [mediaKind, setMediaKind] = useState<'Pdf' | 'Video' | 'Image'>('Pdf');
  const [externalUrl, setExternalUrl] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CorporateDocumentDto | null>(null);
  const [deleting, setDeleting] = useState<CorporateDocumentDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  // Biblioteca (1:1 maqueta): filtro por tipo + "Pill Asignado" (qué pill(s) apuntan a cada archivo).
  // Un documento puede tener MÁS DE UN pill apuntándole (varios pills, mismo targetDocumentId) —
  // por eso esto es .filter(), no .find(): con .find() solo se veía el primero y el resto quedaba
  // oculto en la columna sin ningún indicio de que hubiera más.
  const [typeFilter, setTypeFilter] = useState<LibType>('todos');
  const pillsQ = useDocumentationPills();
  const pillsFor = (docId: string) => (pillsQ.data ?? []).filter((p) => p.targetDocumentId === docId);

  const toggleLanguage = (code: string) =>
    setLanguages((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const resetForm = () => {
    setNombre('');
    setTipo(defaultTipo);
    setScopeOuIds([]);
    setLanguages([]);
    setFile(null);
    setFirstLogin(false);
    setTriggerUpdate(false);
    setMediaKind('Pdf');
    setExternalUrl('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const pickFile = (picked: File | null) => {
    setFormError(null);
    // Al empezar una carga nueva, limpia el feedback (éxito/error) de la anterior para que el banner
    // corresponda al intento actual (evita el aviso verde de un alta previa junto a un formulario nuevo).
    if (upload.isSuccess || upload.isError) upload.reset();
    if (!picked) { setFile(null); return; }
    const isPdf = picked.type === 'application/pdf' || picked.name.toLowerCase().endsWith('.pdf');
    if (!isPdf || picked.size <= 0 || picked.size > MAX_PDF_BYTES) {
      setFormError(t('admin.docs.corp.soloPdf'));
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFile(picked);
  };

  const isMediaUrl = !legalOnly && mediaKind !== 'Pdf'; // Video/Imagen = URL externa (sin archivo)
  const canUpload = nombre.trim().length > 0 && scopeOuIds.length > 0 && !upload.isPending &&
    (isMediaUrl ? externalUrl.trim().length > 0 : !!file);

  const handleUpload = () => {
    setFormError(null);
    if (!nombre.trim()) { setFormError(t('admin.docs.corp.requiereNombre')); return; }
    if (scopeOuIds.length === 0) { setFormError(t('admin.docs.corp.requiereOu')); return; }
    const done = { onSuccess: () => { resetForm(); setShowForm(false); } };
    if (isMediaUrl) {
      const url = externalUrl.trim();
      // Valida URL absoluta http(s) en cliente (feedback inmediato; el backend también la valida).
      let validUrl = false;
      try { const u = new URL(url); validUrl = u.protocol === 'http:' || u.protocol === 'https:'; } catch { validUrl = false; }
      if (!validUrl) { setFormError(t('admin.docs.corp.requiereUrl')); return; }
      upload.mutate({ nombre: nombre.trim(), tipo, scopeOuIds, languages, mediaKind, externalUrl: url }, done);
      return;
    }
    if (!file) { setFormError(t('admin.docs.corp.requiereArchivo')); return; }
    upload.mutate(
      { file, nombre: nombre.trim(), tipo, scopeOuIds, languages, firstLogin: legalOnly && firstLogin, triggerUpdate: legalOnly && triggerUpdate, mediaKind: 'Pdf' },
      done,
    );
  };

  // Cada pestaña muestra solo su clase de documento (legales vs. resto de la biblioteca).
  const docs = (list.data ?? []).filter((d) => isLegalType(d.tipo) === legalOnly);
  // Barra de filtros de la Biblioteca (1:1 maqueta): búsqueda por nombre + filtro por idioma (cliente).
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) =>
      (q === '' || d.nombre.toLowerCase().includes(q)) &&
      (langFilter === '' || d.languages.includes(langFilter)) &&
      (legalOnly || typeFilter === 'todos' || docFileType(d) === typeFilter));
  }, [docs, search, langFilter, typeFilter, legalOnly]);
  const docsPager = usePagination(filtered, 10);
  // Conteos para los sub-tabs de tipo de la Biblioteca.
  const typeCounts = useMemo(() => ({
    todos: docs.length,
    pdf: docs.filter((d) => docFileType(d) === 'pdf').length,
    video: docs.filter((d) => docFileType(d) === 'video').length,
    imagen: docs.filter((d) => docFileType(d) === 'imagen').length,
  }), [docs]);

  // Toggle inline de las banderas legales (1:1 maqueta: switches en la fila, no solo-lectura). El PUT lleva
  // el doc completo con la bandera cambiada (mismo body que el modal), preservando el resto.
  const toggleFlag = (doc: CorporateDocumentDto, flag: 'firstLogin' | 'triggerUpdate') => {
    update.mutate({
      id: doc.id,
      body: {
        nombre: doc.nombre, tipo: doc.tipo, scopeOuIds: doc.scopeOuIds, languages: doc.languages,
        firstLogin: flag === 'firstLogin' ? !doc.firstLogin : (doc.firstLogin ?? false),
        triggerUpdate: flag === 'triggerUpdate' ? !doc.triggerUpdate : (doc.triggerUpdate ?? false),
      },
    });
  };
  const ouName = (id: string) => ouItems.find((o) => o.id === id)?.name ?? id;

  return (
    <div className="admin-docs-corp">
      {/* Banner informativo de Docs Legales (1:1 maqueta): explica el modal de aceptación obligatorio. */}
      {legalOnly && (
        <div className="adm-info-callout">
          <i className="fas fa-circle-info" aria-hidden="true" />
          <p>{t('admin.docs.corp.legalInfo')}</p>
        </div>
      )}
      {/* Header 1:1 maqueta (Docs Legales): "N documentos configurados" + botón que abre el alta en MODAL. */}
      {legalOnly && (
        <div className="admin-docs-list-head">
          <h4 className="admin-docs-corp-list-title">{t('admin.docs.corp.listaCount', { count: docs.length })}</h4>
          <button type="button" className="btn btn--primary" onClick={() => { resetForm(); upload.reset(); setShowForm(true); }}>
            <i className="fas fa-plus" /> {t('admin.docs.corp.agregarDocumento')}
          </button>
        </div>
      )}

      {showForm && (
      <Modal open onClose={() => setShowForm(false)} labelledBy="corp-add-title">
        <ModalHeader onClose={() => setShowForm(false)}>
          <span id="corp-add-title">{legalOnly ? t('admin.docs.corp.agregarDocumento') : t('admin.docs.corp.subirArchivo')}</span>
        </ModalHeader>
        <ModalBody>
      <div className="adm-form-grid">
        <label className="adm-field">
          <span>{t('admin.docs.corp.nombre')}</span>
          <input type="text" value={nombre} maxLength={200} onChange={(e) => setNombre(e.target.value)} />
        </label>
        <label className="adm-field">
          <span>{t('admin.docs.corp.tipo')}</span>
          <select className="field" value={tipo} onChange={(e) => setTipo(e.target.value as CorporateDocType)}>
            {availableTypes.map((ty) => (
              <option key={ty} value={ty}>{t(`admin.docs.corp.tipos.${ty}`)}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Biblioteca: tipo de medio (PDF sube archivo; Video/Imagen usan URL externa). */}
      {!legalOnly && (
        <label className="adm-field">
          <span>{t('admin.docs.corp.mediaKind')}</span>
          <select className="field" value={mediaKind} onChange={(e) => setMediaKind(e.target.value as 'Pdf' | 'Video' | 'Image')}>
            <option value="Pdf">{t('admin.docs.corp.libType.pdf')}</option>
            <option value="Image">{t('admin.docs.corp.libType.imagen')}</option>
            <option value="Video">{t('admin.docs.corp.libType.video')}</option>
          </select>
        </label>
      )}

      {/* GAP-DOC — banderas de aceptación (solo Docs Legales): Primer login + Detonar actualización. */}
      {legalOnly && (
        <div className="adm-toggles-row">
          <label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" checked={firstLogin} onChange={(e) => setFirstLogin(e.target.checked)} /> {t('admin.docs.corp.firstLogin')}</label>
          <label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" checked={triggerUpdate} onChange={(e) => setTriggerUpdate(e.target.checked)} /> {t('admin.docs.corp.triggerUpdate')}</label>
        </div>
      )}

      <div className="adm-field" role="group" aria-labelledby="corp-ou-label">
        <span id="corp-ou-label">{t('admin.docs.corp.visibilidad')}</span>
        <OuTreeSelect items={ouItems} mode="multi" variant="inline" value={scopeOuIds} onChange={setScopeOuIds} />
      </div>

      <div className="adm-field" role="group" aria-labelledby="corp-lang-label">
        <span id="corp-lang-label">{t('admin.docs.corp.idiomas')}</span>
        <LanguageChips value={languages} onToggle={toggleLanguage} />
        <small className="admin-docs-corp-hint">{t('admin.docs.corp.idiomasHint')}</small>
      </div>

      {isMediaUrl ? (
        <label className="adm-field">
          <span>{t('admin.docs.corp.externalUrl')}</span>
          <input type="url" value={externalUrl} placeholder="https://…" onChange={(e) => setExternalUrl(e.target.value)} />
          <small className="admin-docs-corp-hint">{t('admin.docs.corp.externalUrlHint')}</small>
        </label>
      ) : (
        <div className="adm-field">
          <span>{t('admin.docs.corp.archivo')}</span>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <button type="button" className="admin-docs-dropzone" onClick={() => fileRef.current?.click()}>
            <i className={`fas ${file ? 'fa-file-pdf' : 'fa-cloud-arrow-up'}`} />
            <span>{file ? file.name : t('admin.docs.corp.elegirArchivo')}</span>
          </button>
        </div>
      )}

      {formError && (
        <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {formError}</p>
      )}
      {upload.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" />
          {' '}
          {getRecognitionErrorMessage(upload.error, t('admin.docs.corp.errorSubir'))}
        </p>
      )}
      {upload.isSuccess && <p className="adm-success">{t('admin.docs.corp.subidoOk')}</p>}
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn--secondary" onClick={() => setShowForm(false)}>{t('admin.docs.corp.cancelar')}</button>
          <button type="button" className="btn btn--primary" disabled={!canUpload} onClick={handleUpload}>
            <i className={`fas ${upload.isPending ? 'fa-spinner fa-spin' : 'fa-upload'}`} /> {t('admin.docs.corp.subir')}
          </button>
        </ModalFooter>
      </Modal>
      )}
      {/* Barra de Biblioteca 1:1 maqueta: sub-tabs por tipo + búsqueda + idioma + Subir Archivo. */}
      {!legalOnly && (
        <div className="admin-docs-lib-bar">
          <div className="fund-type-tabs" role="tablist" aria-label={t('admin.docs.corp.filterType')}>
            {LIB_TYPE_TABS.map((tt) => (
              <button key={tt.key} type="button" role="tab" aria-selected={typeFilter === tt.key}
                className={`fund-type-tab ${typeFilter === tt.key ? 'fund-type-tab--on' : ''}`}
                onClick={() => setTypeFilter(tt.key)}>
                {t(`admin.docs.corp.libType.${tt.key}`)} ({typeCounts[tt.key]})
              </button>
            ))}
          </div>
          <div className="admin-docs-search">
            <i className="fas fa-magnifying-glass" aria-hidden="true" />
            <input type="text" value={search} placeholder={t('admin.docs.corp.searchPlaceholder')} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="field" value={langFilter} onChange={(e) => setLangFilter(e.target.value)} aria-label={t('admin.docs.corp.filterLang')}>
            <option value="">{t('admin.docs.corp.filterLangAll')}</option>
            {SUPPORTED_LANGUAGE_CODES.map((code) => <option key={code} value={code}>{code.toUpperCase()}</option>)}
          </select>
          <button type="button" className="btn btn--primary" onClick={() => { resetForm(); upload.reset(); setShowForm(true); }}>
            <i className="fas fa-upload" /> {t('admin.docs.corp.subirArchivo')}
          </button>
        </div>
      )}
      {list.isLoading && <p className="admin-docs-corp-empty">{t('common.loading')}</p>}
      {list.isError && (
        <p className="adm-error" role="alert">
          {getRecognitionErrorMessage(list.error, t('admin.docs.corp.errorCargar'))}
        </p>
      )}
      {remove.isError && (
        <p className="adm-error" role="alert">
          {getRecognitionErrorMessage(remove.error, t('admin.docs.corp.errorEliminar'))}
        </p>
      )}
      {!list.isLoading && !list.isError && docs.length === 0 && (
        <p className="admin-docs-corp-empty">{t('admin.docs.corp.vacio')}</p>
      )}
      {docs.length > 0 && filtered.length === 0 && (
        <p className="admin-docs-corp-empty">{t('admin.docs.corp.noResults')}</p>
      )}
      {filtered.length > 0 && (<>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              {legalOnly ? (
              <tr>
                <th>{t('admin.docs.corp.documento')}</th>
                <th>{t('admin.docs.corp.idioma')}</th>
                <th>{t('admin.docs.corp.firstLogin')}</th>
                <th>{t('admin.docs.corp.triggerUpdate')}</th>
                <th>{t('admin.docs.corp.ousActivas')}</th>
                <th>{t('admin.docs.corp.actualizado')}</th>
                <th>{t('admin.docs.corp.acciones')}</th>
              </tr>
              ) : (
              <tr>
                <th aria-label={t('admin.docs.corp.tipo')} />
                <th>{t('admin.docs.corp.nombreArchivo')}</th>
                <th>{t('admin.docs.corp.idioma')}</th>
                <th>{t('admin.docs.corp.pillAsignado')}</th>
                <th>{t('admin.docs.corp.subido')}</th>
                <th>{t('admin.docs.corp.acciones')}</th>
              </tr>
              )}
            </thead>
            <tbody>
              {docsPager.slice.map((doc) => (
                <tr key={doc.id}>
                  {legalOnly ? (<>
                    <td><div className="admin-docs-doc-cell"><i className="fas fa-file-pdf admin-docs-doc-pdf" aria-hidden="true" /><div className="admin-docs-doc-name"><strong>{doc.nombre}</strong><small>{t(`admin.docs.corp.tipos.${doc.tipo}`)}</small></div></div></td>
                    <td className="admin-docs-langcell"><LangVersionsPopover doc={doc} /></td>
                    <td><label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" aria-label={t('admin.docs.corp.firstLogin')} checked={doc.firstLogin ?? false} disabled={update.isPending} onChange={() => toggleFlag(doc, 'firstLogin')} /></label></td>
                    <td><label className="adm-inline-toggle adm-inline-toggle--switch"><input type="checkbox" aria-label={t('admin.docs.corp.triggerUpdate')} checked={doc.triggerUpdate ?? false} disabled={update.isPending} onChange={() => toggleFlag(doc, 'triggerUpdate')} /></label></td>
                    <td>{doc.scopeOuIds.length > 0 ? <div className="admin-docs-ou-badges">{doc.scopeOuIds.map((id) => <span key={id} className="admin-docs-ou-badge">{ouName(id)}</span>)}</div> : '—'}</td>
                    <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                    <td className="admin-docs-corp-row-actions">
                      {/* Solo mientras no haya versiones por idioma: con alguna cargada, doc.url es el
                          archivo raíz (el del alta original) y descargarlo desde aquí sería el
                          documento equivocado — la descarga correcta vive por idioma, en "Versiones". */}
                      {doc.url && Object.keys(doc.files ?? {}).length === 0 && <a className="btn-icon btn-icon--outline" href={doc.url} target="_blank" rel="noopener noreferrer" title={t('admin.docs.corp.descargar')} aria-label={t('admin.docs.corp.descargar')}><i className="fas fa-download" /></a>}
                      <button type="button" className="btn-icon btn-icon--outline" title={t('admin.docs.corp.editar')} aria-label={t('admin.docs.corp.editar')} onClick={() => { update.reset(); setEditing(doc); }}><i className="fas fa-pen" /></button>
                      <button type="button" className="btn-icon btn-icon--outline danger" title={t('admin.docs.corp.eliminar')} aria-label={t('admin.docs.corp.eliminar')} onClick={() => setDeleting(doc)}><i className="fas fa-trash" /></button>
                    </td>
                  </>) : (<>
                    <td><i className={`fas ${LIB_TYPE_ICON[docFileType(doc)]} admin-docs-type-icon admin-docs-type-icon--${docFileType(doc)}`} aria-label={docFileType(doc)} /></td>
                    {/* El nombre que captura el admin en el alta manda sobre el nombre del archivo
                        subido (Docs Legales ya lo hacia asi, aca faltaba) -- doc.fileName solo como
                        respaldo si por lo que sea nombre llegara vacio. */}
                    <td>{doc.nombre || doc.fileName}</td>
                    <td>
                      {doc.languages.length > 0 ? doc.languages.map((l) => <span key={l} className="fund-lang-badge">{l.toUpperCase()}</span>) : <span className="adm-note">—</span>}
                      {/* Versiones por archivo (una por idioma) — solo aplica a PDF; Video/Imagen son URL externa. */}
                      {docFileType(doc) === 'pdf' && (
                        <div className="admin-docs-lang-versions-trigger-wrap">
                          <LangVersionsPopover doc={doc} />
                        </div>
                      )}
                    </td>
                    <td>{(() => { const ps = pillsFor(doc.id); return ps.length > 0 ? <div className="admin-docs-ou-badges">{ps.map((p) => <span key={p.id} className="admin-docs-pill-badge" style={{ background: p.color }}>{pickLocalized(p.titulo, displayLang) || '—'}</span>)}</div> : <span className="adm-note">—</span>; })()}</td>
                    <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                    <td className="admin-docs-corp-row-actions">
                      {doc.url && docFileType(doc) === 'pdf' && <a className="btn-icon btn-icon--outline" href={doc.url} target="_blank" rel="noopener noreferrer" title={t('admin.docs.corp.verVersion')} aria-label={t('admin.docs.corp.verVersion')}><i className="fas fa-eye" /></a>}
                      {doc.url && docFileType(doc) === 'pdf' && <a className="btn-icon btn-icon--outline" href={doc.url} target="_blank" rel="noopener noreferrer" title={t('admin.docs.corp.descargar')} aria-label={t('admin.docs.corp.descargar')}><i className="fas fa-download" /></a>}
                      {doc.url && docFileType(doc) !== 'pdf' && <a className="btn-icon btn-icon--outline" href={doc.url} target="_blank" rel="noopener noreferrer" title={t('admin.docs.corp.abrir')} aria-label={t('admin.docs.corp.abrir')}><i className="fas fa-up-right-from-square" /></a>}
                      {/* Editar (nombre/OU/idiomas): solo PDF por ahora — es el único tipo con "versiones
                          por idioma", que es lo que hace que editar la restricción de idiomas importe;
                          Video/Imagen no tienen ese concepto y no se tocan en este cambio. */}
                      {docFileType(doc) === 'pdf' && (
                        <button type="button" className="btn-icon btn-icon--outline" title={t('admin.docs.corp.editar')} aria-label={t('admin.docs.corp.editar')} onClick={() => { update.reset(); setEditing(doc); }}><i className="fas fa-pen" /></button>
                      )}
                      <button type="button" className="btn-icon btn-icon--outline danger" title={t('admin.docs.corp.eliminar')} aria-label={t('admin.docs.corp.eliminar')} onClick={() => setDeleting(doc)}><i className="fas fa-trash" /></button>
                    </td>
                  </>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagerFooter total={filtered.length} page={docsPager.page} pageSize={docsPager.pageSize} onPage={docsPager.setPage} onPageSize={docsPager.setPageSize} />
      </>)}

      {/* Guardar (Docs Legales, 1:1 maqueta). Los cambios (toggles/versiones) ya persisten al instante;
          este botón re-sincroniza la tabla con el servidor (confirma el estado guardado). */}
      {legalOnly && filtered.length > 0 && (
        <div className="admin-docs-save-row">
          <button type="button" className="btn btn--primary" disabled={list.isFetching} onClick={() => { list.refetch(); }}>
            <i className={`fas ${list.isFetching ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} /> {t('admin.docs.corp.guardar')}
          </button>
        </div>
      )}

      {/* Zona drag-drop de la Biblioteca (1:1 maqueta). Soltar un PDF lo carga y abre el modal de alta. */}
      {!legalOnly && (
        <button
          type="button"
          className="admin-docs-dropzone-lib"
          onClick={() => { resetForm(); upload.reset(); setShowForm(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; resetForm(); upload.reset(); if (f) { pickFile(f); } setShowForm(true); }}
        >
          <i className="fas fa-cloud-arrow-up" aria-hidden="true" />
          <span className="admin-docs-dropzone-lib-main">{t('admin.docs.corp.dropMain')}</span>
          <span className="admin-docs-dropzone-lib-sub">{t('admin.docs.corp.dropSub')}</span>
        </button>
      )}

      {editing && (
        <CorporateDocumentEditModal
          doc={editing}
          ouItems={ouItems}
          availableTypes={availableTypes}
          saving={update.isPending}
          error={update.isError ? getRecognitionErrorMessage(update.error, t('admin.docs.corp.errorActualizar')) : null}
          onClose={() => { setEditing(null); update.reset(); }}
          onSave={(body) => update.mutate({ id: editing.id, body }, { onSuccess: () => { setEditing(null); update.reset(); } })}
        />
      )}
      {deleting && (
        <ConfirmModal
          title={t('admin.docs.corp.eliminar')}
          message={t('admin.docs.corp.eliminarConfirm')}
          confirmText={t('admin.docs.corp.eliminar')}
          onConfirm={() => remove.mutate(deleting.id, { onSettled: () => setDeleting(null) })}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// ─── FASE 4 (§24.4) — Pills de acceso rápido del rail (CPE-162/163/164) ──────────

/** Tipos de destino de una pill (CPE-164). */
const PILL_TYPES: PillContentType[] = ['documento', 'url', 'video'];
/** Íconos Font Awesome ofrecidos (los del maquetado + variantes de uso común). */
const PILL_ICONS: string[] = [
  'fa-lightbulb', 'fa-book-open', 'fa-scroll', 'fa-video', 'fa-file-pdf', 'fa-image',
  'fa-file-lines', 'fa-link', 'fa-star', 'fa-circle-info', 'fa-graduation-cap', 'fa-bullhorn',
  'fa-gift', 'fa-award',
];
const DEFAULT_PILL_COLOR = '#20B15A';

/** Texto localizado con degradación: idioma actual → español → primer idioma con texto → vacío. */
function pickLocalized(map: Record<string, string> | undefined, lang: string): string {
  if (!map) return '';
  return map[lang]?.trim() || map.es?.trim() || Object.values(map).find((v) => v?.trim()) || '';
}

/** Modal de alta/edición de una pill (CPE-162/163/164). */
function PillEditModal({
  pill, ouItems, corpDocs, onClose, onSave, saving, error,
}: Readonly<{
  pill: DocumentationPillDto | null;
  ouItems: OUItem[];
  corpDocs: ReadonlyArray<{ id: string; nombre: string }>;
  onClose: () => void;
  onSave: (body: UpsertPillBody) => void;
  saving: boolean;
  error: string | null;
}>) {
  const { t } = useTranslation('recognition');
  const [titulo, setTitulo] = useState<Record<string, string>>(pill?.titulo ?? {});
  const [tipo, setTipo] = useState<PillContentType>(pill?.tipo ?? 'documento');
  const [icono, setIcono] = useState(pill?.icono ?? PILL_ICONS[0]);
  const [color, setColor] = useState(pill?.color ?? DEFAULT_PILL_COLOR);
  const [scopeOuIds, setScopeOuIds] = useState<string[]>(pill?.scopeOuIds ?? []);
  const [orden, setOrden] = useState(pill?.orden ?? 0);
  const [isActive, setIsActive] = useState(pill?.isActive ?? true);
  const [targetDocumentId, setTargetDocumentId] = useState(pill?.targetDocumentId ?? '');
  const [url, setUrl] = useState(pill?.url ?? '');
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const hasAnyTitle = Object.values(titulo).some((v) => v?.trim());
  const targetOk = tipo === 'documento' ? Boolean(targetDocumentId) : url.trim().length > 0;
  const canSave = hasAnyTitle && targetOk && !saving;

  const sourceLang = SUPPORTED_LANGUAGE_CODES.find((code) => titulo[code]?.trim());
  const handleTranslate = async () => {
    if (!sourceLang || translating) return;
    setTranslateError(null);
    const targetLangs = SUPPORTED_LANGUAGE_CODES.filter((code) => code !== sourceLang);
    setTranslating(true);
    try {
      const response = await translationApi.translateTexts({ sourceLang, targetLangs, texts: [titulo[sourceLang] ?? ''] });
      setTitulo((prev) => {
        const next = { ...prev };
        for (const lang of targetLangs) next[lang] = response.translations[lang]?.[0] ?? next[lang] ?? '';
        return next;
      });
    } catch (err) {
      setTranslateError(
        err instanceof SessionExpiredError
          ? t('admin.translate.sessionExpired')
          : getRecognitionErrorMessage(err, t('admin.docs.translateError', 'No se pudo traducir automáticamente.')),
      );
    } finally {
      setTranslating(false);
    }
  };

  const submit = () => {
    if (!canSave) return;
    onSave({
      titulo,
      tipo,
      icono,
      color,
      orden,
      scopeOuIds,
      isActive,
      targetDocumentId: tipo === 'documento' ? targetDocumentId : null,
      url: tipo === 'documento' ? null : url.trim(),
    });
  };

  return (
    <Modal open onClose={onClose} labelledBy="pill-edit-title">
      <ModalHeader onClose={onClose}>
        <span id="pill-edit-title">{pill ? t('admin.docs.pills.editar') : t('admin.docs.pills.nueva')}</span>
      </ModalHeader>
      <ModalBody>
        <div className="adm-pill-preview-row">
          <span className="adm-pill-preview" style={{ background: color }}>
            <i className={`fas ${icono}`} aria-hidden="true" />
          </span>
          <span className="adm-pill-preview-label">{pickLocalized(titulo, 'es') || t('admin.docs.pills.sinTitulo')}</span>
        </div>

        <LocalizedFields
          label={t('admin.docs.pills.titulo')}
          value={titulo}
          onChange={setTitulo}
        />
        <div className="admin-docs-actions">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={!sourceLang || translating}
            onClick={handleTranslate}
          >
            <i className={`fas ${translating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
            {' '}
            {translating ? t('admin.docs.translating', 'Traduciendo…') : t('admin.docs.translate', 'Traducir a los demás idiomas')}
          </button>
        </div>
        {translateError && (
          <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {translateError}</p>
        )}

        <div className="adm-form-grid">
          <label className="adm-field">
            <span>{t('admin.docs.pills.tipo')}</span>
            <select className="field" value={tipo} onChange={(e) => setTipo(e.target.value as PillContentType)}>
              {PILL_TYPES.map((ty) => (
                <option key={ty} value={ty}>{t(`admin.docs.pills.tipos.${ty}`)}</option>
              ))}
            </select>
          </label>
          <label className="adm-field">
            <span>{t('admin.docs.pills.icono')}</span>
            <select className="field" value={icono} onChange={(e) => setIcono(e.target.value)}>
              {PILL_ICONS.map((ic) => (
                <option key={ic} value={ic}>{ic.replace('fa-', '')}</option>
              ))}
            </select>
          </label>
          <label className="adm-field">
            <span>{t('admin.docs.pills.color')}</span>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          </label>
          <label className="adm-field">
            <span>{t('admin.docs.pills.orden')}</span>
            <input type="number" min={0} value={orden} onChange={(e) => setOrden(Math.max(0, Number(e.target.value) || 0))} />
          </label>
        </div>

        {tipo === 'documento' ? (
          <label className="adm-field">
            <span>{t('admin.docs.pills.documento')}</span>
            <select className="field" value={targetDocumentId} onChange={(e) => setTargetDocumentId(e.target.value)}>
              <option value="">{t('admin.docs.pills.elegirDocumento')}</option>
              {corpDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
            {corpDocs.length === 0 && (
              <small className="admin-docs-corp-hint">{t('admin.docs.pills.sinDocumentos')}</small>
            )}
          </label>
        ) : (
          <label className="adm-field">
            <span>{t('admin.docs.pills.url')}</span>
            <input
              type="url"
              value={url}
              placeholder="https://…"
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
        )}

        <div className="adm-field" role="group" aria-labelledby="pill-ou-label">
          <span id="pill-ou-label">{t('admin.docs.pills.visibilidad')}</span>
          <OuTreeSelect items={ouItems} mode="multi" variant="inline" value={scopeOuIds} onChange={setScopeOuIds} />
          <small className="admin-docs-corp-hint">{t('admin.docs.pills.visibilidadHint')}</small>
        </div>

        <label className="adm-check">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span>{t('admin.docs.pills.activa')}</span>
        </label>

        {error && (
          <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {error}</p>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn--secondary" onClick={onClose}>{t('admin.docs.corp.cancelar')}</button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={submit}>
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} /> {t('admin.docs.corp.guardar')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Diagnóstico del vínculo pill→documento para la columna "Documento" de la tabla de admin.
 *
 * Bug real (2026-09-04, reproducido en local): `pill.scopeOuIds` y `documento.ScopeOuIds` son dos
 * campos independientes, en dos microservicios distintos, sin ninguna validación cruzada en NINGÚN
 * flujo (alta/edición de la pill, alta/edición/baja del documento). Un admin puede perfectamente
 * configurar una pill visible para 2 OUs apuntando a un documento cuyo alcance real es otro —
 * `documentation-pills/for-me` responde 200 con `url: null` y el usuario final ve "Error" al abrir
 * la pill, sin que ningún admin se entere hasta que alguien lo reporta. Esta función NO arregla esa
 * falta de validación (viviría en el backend, cruzando dos servicios) — solo hace visible el
 * síntoma en la pantalla que YA tiene ambos datos cargados (`corpDocs` en la misma consulta que
 * pinta esta tabla), para que un admin lo detecte y lo corrija antes de que llegue a un usuario.
 */
type PillDocumentStatus =
  | { kind: 'not-applicable' }
  | { kind: 'ok'; nombre: string }
  | { kind: 'missing' }
  | { kind: 'partial-scope'; nombre: string };

function getPillDocumentStatus(
  pill: DocumentationPillDto,
  docsById: ReadonlyMap<string, CorporateDocumentDto>,
): PillDocumentStatus {
  if (pill.tipo !== 'documento') return { kind: 'not-applicable' };
  const doc = pill.targetDocumentId ? docsById.get(pill.targetDocumentId) : undefined;
  // Documento borrado/desactivado desde que se configuró la pill, o id nunca resuelto: el listado de
  // admin (`useCorporateDocuments`) solo trae documentos ACTIVOS, así que "no está en el mapa" ya
  // cubre ambos casos sin distinguirlos — para un admin, el remedio es el mismo: revisar la pill.
  if (!doc) return { kind: 'missing' };
  // Documento SIN alcance (scopeOuIds vacío) = visible para todas las OUs, cualquier pill calza.
  // Con alcance, la pill promete a MÁS gente de la que el documento realmente cubre si le falta
  // aunque sea una sola de las OUs de la pill.
  const cubreTodo = doc.scopeOuIds.length === 0
    || pill.scopeOuIds.every((ouId) => doc.scopeOuIds.includes(ouId));
  return cubreTodo ? { kind: 'ok', nombre: doc.nombre } : { kind: 'partial-scope', nombre: doc.nombre };
}

/**
 * Celda de la columna "Documento": plano si `tipo !== 'documento'`, con el mismo aviso ya usado en
 * el resto del panel (`.adm-error`, ver `errorCargar`/`errorGuardar` arriba) si el vínculo no resuelve
 * — reutiliza el color/ícono existentes en vez de introducir un estilo de advertencia nuevo.
 */
function PillDocumentCell({ status }: Readonly<{ status: PillDocumentStatus }>) {
  const { t } = useTranslation('recognition');
  if (status.kind === 'not-applicable') return <>—</>;
  if (status.kind === 'ok') return <>{status.nombre}</>;
  const warning = status.kind === 'missing'
    ? t('admin.docs.pills.documentoNoEncontrado')
    : t('admin.docs.pills.documentoAlcanceIncompleto');
  return (
    <p className="adm-error" role="alert" title={warning}>
      <i className="fas fa-triangle-exclamation" aria-hidden="true" />
      {status.kind === 'partial-scope' ? `${status.nombre} — ` : ''}{warning}
    </p>
  );
}

/** CPE-162/163/164 — CRUD de pills de acceso rápido (se muestran en el rail de la landing). */
function PillsManager() {
  const { t, i18n } = useTranslation('recognition');
  const displayLang = i18n.language?.split('-')[0] ?? 'es';
  const list = useDocumentationPills();
  const create = useCreatePill();
  const update = useUpdatePill();
  const remove = useDeletePill();
  const { data: ouItems = [] } = useOUItems();
  // Para el selector de tipo "documento": los PDFs corporativos disponibles (id + nombre).
  const corpList = useCorporateDocuments();
  const corpDocs = (corpList.data ?? []).map((d) => ({ id: d.id, nombre: d.nombre }));
  // Mismo listado, SIN reducir (con scopeOuIds): para diagnosticar en la tabla si el documento que
  // una pill referencia realmente cubre el alcance que la pill promete — ver getPillDocumentStatus.
  const corpDocsById = useMemo(
    () => new Map((corpList.data ?? []).map((d) => [d.id, d] as const)),
    [corpList.data],
  );

  const [editing, setEditing] = useState<DocumentationPillDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DocumentationPillDto | null>(null);

  const pills = list.data ?? [];
  const pillsPager = usePagination(pills, 10);

  // Edición INLINE de la fila (1:1 maqueta: color/orden/activa en la fila). El PUT lleva el pill completo con
  // el campo cambiado (mismo body que el modal), preservando el resto.
  const patchPill = (pill: DocumentationPillDto, partial: Partial<UpsertPillBody>) => {
    update.mutate({
      id: pill.id,
      body: {
        titulo: pill.titulo, tipo: pill.tipo, icono: pill.icono, color: pill.color, orden: pill.orden,
        scopeOuIds: pill.scopeOuIds, isActive: pill.isActive,
        targetDocumentId: pill.targetDocumentId ?? null, url: pill.url ?? null,
        ...partial,
      },
    });
  };
  const closeModal = () => { setEditing(null); setCreating(false); create.reset(); update.reset(); };

  const handleSave = (body: UpsertPillBody) => {
    if (editing) {
      update.mutate({ id: editing.id, body }, { onSuccess: closeModal });
    } else {
      create.mutate(body, { onSuccess: closeModal });
    }
  };

  const activeMutation = editing ? update : create;
  const saveError = activeMutation.isError
    ? getRecognitionErrorMessage(activeMutation.error, t('admin.docs.pills.errorGuardar'))
    : null;

  return (
    <div className="admin-docs-corp">
      {/* Header de segundo nivel del tab Pills (1:1 maqueta): título + bajada descriptiva + botón. */}
      <div className="admin-docs-pills-head">
        <div>
          <h4 className="admin-docs-pills-title">{t('admin.docs.pills.sectionTitle')}</h4>
          <p className="admin-docs-pills-subtitle">{t('admin.docs.pills.sectionSubtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => { create.reset(); setCreating(true); }}>
          <i className="fas fa-plus" /> {t('admin.docs.pills.nueva')}
        </button>
      </div>

      {list.isLoading && <p className="admin-docs-corp-empty">{t('common.loading')}</p>}
      {list.isError && (
        <p className="adm-error" role="alert">
          {getRecognitionErrorMessage(list.error, t('admin.docs.pills.errorCargar'))}
          {' '}
          <button type="button" className="btn btn--link" onClick={() => { list.refetch(); }}>{t('common.retry')}</button>
        </p>
      )}
      {remove.isError && (
        <p className="adm-error" role="alert">{getRecognitionErrorMessage(remove.error, t('admin.docs.pills.errorEliminar'))}</p>
      )}
      {/* Errores de edición inline (toggle/ícono/color/orden): sin esto un patch fallido se revertía en silencio. */}
      {!editing && !creating && update.isError && (
        <p className="adm-error" role="alert">{getRecognitionErrorMessage(update.error, t('admin.docs.pills.errorGuardar'))}</p>
      )}
      {!list.isLoading && !list.isError && pills.length === 0 && (
        <p className="admin-docs-corp-empty">{t('admin.docs.pills.vacio')}</p>
      )}

      {pills.length > 0 && (<>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.docs.pills.vistaPrevia')}</th>
                <th>{t('admin.docs.pills.nombre')}</th>
                <th>{t('admin.docs.pills.icono')}</th>
                <th>{t('admin.docs.pills.color')}</th>
                <th>{t('admin.docs.pills.tipoContenido')}</th>
                <th>{t('admin.docs.pills.documentoColumna')}</th>
                <th>{t('admin.docs.pills.activaSi')}</th>
                <th>{t('admin.docs.pills.orden')}</th>
                <th>{t('admin.docs.corp.acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {pillsPager.slice.map((pill) => (
                <tr key={pill.id}>
                  <td>
                    <span className="adm-pill-preview" style={{ background: pill.color }}>
                      <i className={`fas ${pill.icono}`} aria-hidden="true" />
                    </span>
                  </td>
                  <td className="adm-pill-name">{pickLocalized(pill.titulo, displayLang) || '—'}</td>
                  <td>
                    <select className="adm-select adm-select--sm" defaultValue={pill.icono} key={`i-${pill.id}-${pill.icono}`} disabled={update.isPending}
                      aria-label={t('admin.docs.pills.icono')}
                      onChange={(e) => { if (e.target.value !== pill.icono) patchPill(pill, { icono: e.target.value }); }}>
                      {PILL_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="color" className="adm-inline-color" defaultValue={pill.color} key={`c-${pill.id}-${pill.color}`} disabled={update.isPending}
                      aria-label={t('admin.docs.pills.color')}
                      onBlur={(e) => { if (e.target.value !== pill.color) patchPill(pill, { color: e.target.value }); }} />
                  </td>
                  <td>
                    <select className="adm-select adm-select--sm" value={pill.tipo} disabled={update.isPending}
                      aria-label={t('admin.docs.pills.tipoContenido')}
                      onChange={(e) => { const v = e.target.value as PillContentType; if (v !== pill.tipo) { update.reset(); setEditing({ ...pill, tipo: v }); } }}>
                      {PILL_TYPES.map((ty) => <option key={ty} value={ty}>{t(`admin.docs.pills.tipos.${ty}`)}</option>)}
                    </select>
                  </td>
                  <td>
                    <PillDocumentCell status={getPillDocumentStatus(pill, corpDocsById)} />
                  </td>
                  <td>
                    <label className="adm-inline-toggle adm-inline-toggle--switch">
                      <input type="checkbox" aria-label={t('admin.docs.pills.activa')} checked={pill.isActive} disabled={update.isPending} onChange={() => patchPill(pill, { isActive: !pill.isActive })} />
                    </label>
                  </td>
                  <td>
                    <input type="number" min={0} className="adm-inline-num" defaultValue={pill.orden} key={`o-${pill.id}-${pill.orden}`} disabled={update.isPending}
                      aria-label={t('admin.docs.pills.orden')}
                      onBlur={(e) => { const v = Math.max(0, Math.round(Number(e.target.value) || 0)); if (v !== pill.orden) patchPill(pill, { orden: v }); }} />
                  </td>
                  <td className="admin-docs-corp-row-actions">
                    <button type="button" className="btn-icon btn-icon--outline" title={t('admin.docs.corp.editar')} aria-label={t('admin.docs.corp.editar')} onClick={() => { update.reset(); setEditing(pill); }}>
                      <i className="fas fa-pen" />
                    </button>
                    <button type="button" className="btn-icon btn-icon--outline danger" title={t('admin.docs.corp.eliminar')} aria-label={t('admin.docs.corp.eliminar')} onClick={() => setDeleting(pill)}>
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagerFooter total={pills.length} page={pillsPager.page} pageSize={pillsPager.pageSize} onPage={pillsPager.setPage} onPageSize={pillsPager.setPageSize} />
      </>)}

      {(creating || editing) && (
        <PillEditModal
          key={editing?.id ?? 'new'}
          pill={editing}
          ouItems={ouItems}
          corpDocs={corpDocs}
          saving={create.isPending || update.isPending}
          error={saveError}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
      {deleting && (
        <ConfirmModal
          title={t('admin.docs.pills.eliminar')}
          message={t('admin.docs.pills.eliminarConfirm')}
          confirmText={t('admin.docs.corp.eliminar')}
          onConfirm={() => remove.mutate(deleting.id, { onSettled: () => setDeleting(null) })}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// GAP-DOC (§26.11) — réplica de la maqueta "Gestión de Documentación": 3 tabs. Los legales dejaron de ser
// un editor de texto por-tab y pasaron a la tabla-PDF de "Docs Legales" (Privacidad/Bases/Cookies/Aviso).
type DocsTab = 'pills' | 'biblioteca' | 'legales';

const DOCS_TABS: ReadonlyArray<{ id: DocsTab; icon: string; labelKey: string }> = [
  { id: 'pills', icon: '💊', labelKey: 'admin.docs.pillsTab' },
  { id: 'biblioteca', icon: '📁', labelKey: 'admin.docs.bibliotecaTab' },
  { id: 'legales', icon: '⚖️', labelKey: 'admin.docs.legalesTab' },
];

/** Bloque "Documentación" (REQ-137/063 + RF-079): texto legal (borrador→publicar) + PDFs corporativos. */
export function AdminDocumentacion() {
  const { t } = useTranslation('recognition');
  const [activeTab, setActiveTab] = useState<DocsTab>('pills');

  // Navegación por flechas entre subtabs (patrón WAI-ARIA Tabs con roving tabindex).
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = DOCS_TABS.findIndex((tab) => tab.id === activeTab);
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = DOCS_TABS[(index + delta + DOCS_TABS.length) % DOCS_TABS.length];
    setActiveTab(next.id);
    document.getElementById(`docs-tab-${next.id}`)?.focus();
  };

  return (
    <AdminSection title={t('admin.docs.gestionTitle')} description={t('admin.docs.gestionDesc')}>
      <div className="admin-docs-panel">
        <div className="adm-subtabs" role="tablist" aria-label={t('admin.nav.docs')}>
          {DOCS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`docs-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls="docs-panel"
              tabIndex={activeTab === tab.id ? 0 : -1}
              className={`adm-subtab ${activeTab === tab.id ? 'adm-subtab--on' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={onTabKeyDown}
            >
              <span className="adm-subtab-emoji" aria-hidden="true">{tab.icon}</span> {t(tab.labelKey)}
            </button>
          ))}
        </div>
        <div role="tabpanel" id="docs-panel" aria-labelledby={`docs-tab-${activeTab}`}>
          {activeTab === 'pills' && <PillsManager />}
          {activeTab === 'biblioteca' && <CorporateDocumentsManager legalOnly={false} key="biblioteca" />}
          {activeTab === 'legales' && <CorporateDocumentsManager legalOnly key="legales" />}
        </div>
      </div>
    </AdminSection>
  );
}

export default AdminDocumentacion;
